// Heuristic mapping from raw cv_extract_profile failure reasons to
// user-facing copy. The raw `reason` field comes from `reparseProfile()`
// in UploadCV.tsx and the equivalent `autofillFromCv()` in Profile.tsx.
// Both are kept duplicated; this helper is the only shared piece.

export interface FriendlyExtractError {
  /** Short heading for the error card. */
  title: string;
  /** Body copy explaining the likely cause + recommended action. */
  body: string;
}

/**
 * Map a raw reason string to a friendly message pair.
 *
 * Defaults (and the most common case in practice) is the LLM endpoint
 * being unreachable — that's what `cv_extract_profile` reports as
 * "job failed" when Ollama returns HTTP 530 / fetch errors.
 */
export function friendlyExtractError(rawReason: string | undefined | null): FriendlyExtractError {
  const r = (rawReason || '').toLowerCase().trim();
  if (r.startsWith('submit')) {
    return {
      title: 'Server rejected the job',
      body: 'This usually means your session expired or you hit a daily quota. Try signing out and back in. If the same error returns, ping the maintainer.',
    };
  }
  if (r === 'timeout' || r.includes('timed out')) {
    return {
      title: 'LLM took too long',
      body: 'The home Ollama may still be loading the model into VRAM. First-time loads can take a minute. Wait a few seconds and click Retry.',
    };
  }
  if (r.includes('vanish')) {
    return {
      title: 'Job worker disappeared',
      body: 'The server may have restarted mid-run. Click Retry — your CV file is already saved.',
    };
  }
  // Default: HTTP 530 from Cloudflare or any other connectivity blip.
  return {
    title: "Couldn't reach the LLM",
    body: 'The home Ollama tunnel looks unreachable right now (often a brief Cloudflare hiccup or the Windows box went to sleep). Click Retry in a moment — your CV file is already saved on the server.',
  };
}
